import React, { useState, useMemo, useEffect } from 'react';
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  Activity, 
  AlertTriangle, 
  RefreshCw, 
  BarChart3, 
  PieChart, 
  Target, 
  Clock,
  Server,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Minus
} from 'lucide-react';
import apiClient from '../lib/apiClient';

const GlobalAdminDashboard = ({ 
  data, 
  loading, 
  apiConnected, 
  onRefreshData, 
  error, 
  user,
  tenants,           
  selectedTenant,    
  onTenantChange    
}) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('30d');
  const [selectedMetricView, setSelectedMetricView] = useState('overview');

  // Calculate derived metrics and trends
  const derivedMetrics = useMemo(() => {
    if (!data.platformMetrics) return null;

    const {
      totalLeadsProcessed = 0,
      totalHandoffs = 0,
      totalPipelineValue = 0,
      avgTimeToHandoff = 0,
      totalPartners = 0
    } = data.platformMetrics || {};

    return {
      leadToHandoffRate: totalLeadsProcessed > 0 ? (totalHandoffs / totalLeadsProcessed * 100).toFixed(2) : '0.00',
      avgPipelineValuePerHandoff: totalHandoffs > 0 ? (totalPipelineValue / totalHandoffs).toFixed(0) : '0',
      leadsPerPartner: totalPartners > 0 ? Math.round(totalLeadsProcessed / totalPartners) : 0,
      handoffsPerPartner: totalPartners > 0 ? Math.round(totalHandoffs / totalPartners) : 0
    };
  }, [data.platformMetrics]);

  // Format large numbers
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };


  // Render trend indicator
  const TrendIndicator = ({ value, isPositive = true, suffix = '%' }) => {
    if (!value) return <span className="text-gray-400">-</span>;
    
    const Icon = isPositive ? ArrowUpRight : ArrowDownRight;
    const colorClass = isPositive ? 'text-green-600' : 'text-red-600';
    
    return (
      <div className={`flex items-center ${colorClass}`}>
        <Icon className="h-4 w-4 mr-1" />
        <span className="text-sm font-medium">{value}{suffix}</span>
      </div>
    );
  };

  // Platform Performance Metrics Component
  const PlatformPerformanceSection = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Platform Performance & Value</h2>
        <div className="flex items-center space-x-2">
          <select 
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1 text-sm"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="1y">Last Year</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Leads Processed"
          value={formatNumber(data.platformMetrics?.totalLeadsProcessed)}
          trend={data.platformMetrics?.leadsProcessedTrend}
          icon={<Target className="h-6 w-6 text-blue-600" />}
          description="All leads entered across all partners"
        />
        
        <MetricCard
          title="Overall Lead-to-Handoff Rate"
          value={`${derivedMetrics?.leadToHandoffRate || '0'}%`}
          trend={data.platformMetrics?.handoffRateTrend}
          icon={<TrendingUp className="h-6 w-6 text-green-600" />}
          description="Critical platform success metric"
          highlighted
        />
        
        <MetricCard
          title="Pipeline Conversion Rate"
          value={`${data.platformMetrics?.pipelineConversionRate || '0'}%`}
          trend={data.platformMetrics?.pipelineConversionTrend}
          icon={<BarChart3 className="h-6 w-6 text-purple-600" />}
          description="Handoffs that become pipeline value"
        />
        
        <MetricCard
          title="Total Pipeline Value"
          value={formatCurrency(data.platformMetrics?.totalPipelineValue)}
          trend={data.platformMetrics?.pipelineValueTrend}
          icon={<DollarSign className="h-6 w-6 text-emerald-600" />}
          description="Financial proof of platform worth"
          highlighted
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform Efficiency</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Average Time-to-Handoff</span>
              <span className="font-medium">{data.platformMetrics?.avgTimeToHandoff || '0'} min</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Average Pipeline Value per Handoff</span>
              <span className="font-medium">{formatCurrency(derivedMetrics?.avgPipelineValuePerHandoff)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Leads per Partner (Avg)</span>
              <span className="font-medium">{derivedMetrics?.leadsPerPartner || '0'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Handoffs per Partner (Avg)</span>
              <span className="font-medium">{derivedMetrics?.handoffsPerPartner || '0'}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Lead Processing Trends</h3>
          {data.leadProcessingTrends ? (
            <div className="space-y-3">
              {data.leadProcessingTrends.map((trend, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{trend.date}</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">{formatNumber(trend.leads)} leads</span>
                    <TrendIndicator 
                      value={trend.change} 
                      isPositive={trend.change > 0}
                      suffix=""
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>Processing trends will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Partner & Business Health Section
  const PartnerHealthSection = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Partner & Business Health</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Partner Leaderboard</h3>
            {data.partnerLeaderboard && data.partnerLeaderboard.length > 0 ? (
              <div className="space-y-3">
                {data.partnerLeaderboard.slice(0, 10).map((partner, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-800">{idx + 1}</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{partner.name}</p>
                        <p className="text-sm text-gray-500">{partner.industry || 'Various'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">{formatNumber(partner.totalHandoffs)} handoffs</p>
                      <p className="text-sm text-gray-500">{formatCurrency(partner.pipelineValue)} pipeline</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Partner performance data loading...</p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <MetricCard
            title="Partner Churn Rate"
            value={`${data.partnerChurnAnalysis?.churnRate || '0'}%`}
            trend={data.partnerChurnAnalysis?.churnTrend}
            icon={<AlertTriangle className="h-6 w-6 text-red-600" />}
            description="Monthly partner churn"
          />
          
          <MetricCard
            title="New Partner Acquisition"
            value={formatNumber(data.partnerChurnAnalysis?.newPartners)}
            trend={data.partnerChurnAnalysis?.acquisitionTrend}
            icon={<Users className="h-6 w-6 text-blue-600" />}
            description="New partners this period"
          />

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="font-medium text-gray-900 mb-3">Feature Adoption</h4>
            {data.featureAdoptionMetrics ? (
              <div className="space-y-2">
                {Object.entries(data.featureAdoptionMetrics).map(([feature, adoption]) => (
                  <div key={feature} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 capitalize">{feature.replace('_', ' ')}</span>
                    <span className="text-sm font-medium">{adoption}%</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Loading adoption metrics...</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Financial & Business Metrics Section
  const FinancialMetricsSection = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Financial & Business Metrics</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Monthly Recurring Revenue"
          value={formatCurrency(data.financialMetrics?.mrr)}
          trend={data.financialMetrics?.mrrTrend}
          icon={<DollarSign className="h-6 w-6 text-green-600" />}
          description="Total monthly revenue"
          highlighted
        />
        
        <MetricCard
          title="Customer LTV"
          value={formatCurrency(data.financialMetrics?.ltv)}
          trend={data.financialMetrics?.ltvTrend}
          icon={<TrendingUp className="h-6 w-6 text-purple-600" />}
          description="Average lifetime value"
        />
        
        <MetricCard
          title="Customer Acquisition Cost"
          value={formatCurrency(data.financialMetrics?.cac)}
          trend={data.financialMetrics?.cacTrend}
          icon={<Target className="h-6 w-6 text-orange-600" />}
          description="Average cost to acquire"
        />
        
        <MetricCard
          title="LTV:CAC Ratio"
          value={`${data.financialMetrics?.ltvCacRatio || '0'}:1`}
          trend={data.financialMetrics?.ltvCacTrend}
          icon={<BarChart3 className="h-6 w-6 text-blue-600" />}
          description="Business health indicator"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Breakdown</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">New MRR</span>
              <span className="font-medium text-green-600">+{formatCurrency(data.financialMetrics?.newMrr)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Expansion MRR</span>
              <span className="font-medium text-blue-600">+{formatCurrency(data.financialMetrics?.expansionMrr)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Churned MRR</span>
              <span className="font-medium text-red-600">-{formatCurrency(data.financialMetrics?.churnedMrr)}</span>
            </div>
            <hr className="my-2" />
            <div className="flex items-center justify-between font-semibold">
              <span className="text-gray-900">Net MRR Growth</span>
              <span className="text-green-600">+{formatCurrency(data.financialMetrics?.netMrrGrowth)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Health</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Gross Margin</span>
              <span className="font-medium">{data.financialMetrics?.grossMargin || '0'}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">MRR Churn Rate</span>
              <span className="font-medium">{data.financialMetrics?.mrrChurnRate || '0'}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Revenue per Partner</span>
              <span className="font-medium">{formatCurrency(data.financialMetrics?.revenuePerPartner)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Payback Period</span>
              <span className="font-medium">{data.financialMetrics?.paybackPeriod || '0'} months</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // System Health & Technical Performance Section
  const SystemHealthSection = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">System Health & Technical Performance</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="System Uptime"
          value={`${data.systemHealth?.uptime || '99.9'}%`}
          trend={data.systemHealth?.uptimeTrend}
          icon={<Server className="h-6 w-6 text-green-600" />}
          description="Platform availability"
        />
        
        <MetricCard
          title="AI Agent Latency"
          value={`${data.systemHealth?.aiLatency || '0'}ms`}
          trend={data.systemHealth?.latencyTrend}
          icon={<Zap className="h-6 w-6 text-yellow-600" />}
          description="Average processing time"
        />
        
        <MetricCard
          title="API Response Time"
          value={`${data.systemHealth?.apiResponseTime || '0'}ms`}
          trend={data.systemHealth?.apiResponseTrend}
          icon={<Activity className="h-6 w-6 text-blue-600" />}
          description="API performance"
        />
        
        <MetricCard
          title="Error Rate"
          value={`${data.systemHealth?.errorRate || '0'}%`}
          trend={data.systemHealth?.errorTrend}
          icon={<AlertTriangle className="h-6 w-6 text-red-600" />}
          description="System error percentage"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Resource Utilization</h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-600">CPU Usage</span>
                <span className="text-sm font-medium">{data.systemHealth?.cpuUsage || '0'}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full" 
                  style={{ width: `${data.systemHealth?.cpuUsage || 0}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-600">Memory Usage</span>
                <span className="text-sm font-medium">{data.systemHealth?.memoryUsage || '0'}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full" 
                  style={{ width: `${data.systemHealth?.memoryUsage || 0}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-600">Database Load</span>
                <span className="text-sm font-medium">{data.systemHealth?.dbLoad || '0'}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-purple-600 h-2 rounded-full" 
                  style={{ width: `${data.systemHealth?.dbLoad || 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent System Events</h3>
          {data.systemHealth?.recentEvents ? (
            <div className="space-y-3">
              {data.systemHealth.recentEvents.slice(0, 5).map((event, idx) => (
                <div key={idx} className="flex items-start space-x-3">
                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                    event.type === 'error' ? 'bg-red-500' :
                    event.type === 'warning' ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`}></div>
                  <div>
                    <p className="text-sm text-gray-900">{event.message}</p>
                    <p className="text-xs text-gray-500">{event.timestamp}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              <Activity className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">System events will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Reusable Metric Card Component
  const MetricCard = ({ title, value, trend, icon, description, highlighted = false }) => (
    <div className={`bg-white rounded-lg border p-6 ${highlighted ? 'border-blue-200 bg-blue-50' : 'border-gray-200'}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className={`text-2xl font-bold ${highlighted ? 'text-blue-900' : 'text-gray-900'}`}>
            {value}
          </p>
          {description && (
            <p className="text-xs text-gray-500 mt-1">{description}</p>
          )}
        </div>
        <div className="ml-4">
          {icon}
        </div>
      </div>
      {trend && (
        <div className="mt-3">
          <TrendIndicator 
            value={Math.abs(trend)} 
            isPositive={trend > 0}
          />
        </div>
      )}
    </div>
  );

  // Handle loading state
  if (loading && (!data.platformMetrics && !data.partnerLeaderboard)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Global Analytics</h2>
          <p className="text-gray-600">Aggregating cross-platform performance data...</p>
        </div>
      </div>
    );
  }

  // Main Dashboard Layout
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Global Admin Dashboard</h1>
              <p className="text-gray-600 mt-1">
                Comprehensive platform analytics and partner performance insights
              </p>
            </div>
            <div className="flex items-center space-x-4">
 <select 
  value={selectedTenant}
  onChange={(e) => onTenantChange(e.target.value)}  // Use the prop function
  className="border border-gray-300 rounded-md px-3 py-1 text-sm"
>
  <option value="all">All Tenants</option>
  {tenants && tenants.map(tenant => (  // Use the tenants prop
    <option key={tenant.id} value={tenant.id}>
      {tenant.name}
    </option>
  ))}
</select>
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${apiConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className="text-sm text-gray-600">
                  {apiConnected ? 'Connected' : 'Disconnected'}
                </span>
              </div>
              <button
                onClick={onRefreshData}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh Data
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error Loading Data</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-12">
          <PlatformPerformanceSection />
          <PartnerHealthSection />
          <FinancialMetricsSection />
          <SystemHealthSection />
        </div>
      </div>
    </div>
  );
};

export default GlobalAdminDashboard;